import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, Col, Loading, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { LucidePlus, LucideTrash2 } from 'lucide-react';

import { FreightLaneCostForm } from './FreightLaneCostForm';

import { useDeleteFreightLaneCostMutation } from '~/modules/transportation/hooks/mutations/useDeleteFreightLaneCostMutation';
import { useFreightLaneCostsListQuery } from '~/modules/transportation/hooks/queries/useFreightLaneCostsListQuery';
import { FreightLaneCostRecord } from '~/modules/transportation/types';

type FreightLaneCostHistoryProps = {
  freightLaneId: string;
};

export const FreightLaneCostHistory = ({
  freightLaneId,
}: FreightLaneCostHistoryProps) => {
  const t = useTranslations();
  const [showForm, setShowForm] = React.useState(false);
  const [editingCost, setEditingCost] =
    React.useState<FreightLaneCostRecord | null>(null);
  const toast = useToast();

  const { data, isLoading, refetch } = useFreightLaneCostsListQuery({
    filter: { freightLaneId },
    sorting: ['StartDateDesc' as const],
    pagination: { limit: 100, offset: 0 },
  });

  const { mutateAsync: deleteCost, isPending: isDeleting } =
    useDeleteFreightLaneCostMutation();

  const costs = React.useMemo(
    () => data?.freightLaneCosts ?? [],
    [data?.freightLaneCosts],
  );

  const handleAddCost = React.useCallback(() => {
    setEditingCost(null);
    setShowForm(true);
  }, []);

  const handleEditCost = React.useCallback((cost: FreightLaneCostRecord) => {
    setEditingCost(cost);
    setShowForm(true);
  }, []);

  const handleDeleteCost = React.useCallback(
    async (costId: string) => {
      try {
        await deleteCost({ id: costId });
        toast.success(t.freightLanes.costs.messages.deleteSuccess);
        refetch();
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t.freightLanes.costs.messages.error,
        );
      }
    },
    [deleteCost, toast, t, refetch],
  );

  const handleFormSuccess = React.useCallback(() => {
    setShowForm(false);
    setEditingCost(null);
    refetch();
  }, [refetch]);

  const handleFormCancel = React.useCallback(() => {
    setShowForm(false);
    setEditingCost(null);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Col gap4 className="@FreightLaneCostHistory">
      <Row className="items-center justify-between">
        <div className="text-sm font-medium">{t.freightLanes.costs.title}</div>
        <Button variant="outline" size="small" onPress={handleAddCost}>
          <LucidePlus size={14} />
          {t.freightLanes.costs.add}
        </Button>
      </Row>

      {showForm && (
        <FreightLaneCostForm
          freightLaneId={freightLaneId}
          cost={editingCost ?? undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {costs.length === 0 && !showForm && (
        <div className="py-4 text-center text-sm text-neutral/50">
          {t.freightLanes.costs.empty}
        </div>
      )}

      {costs.map((cost) => (
        <div
          key={cost.id}
          className="cursor-pointer rounded-md border p-3 hover:bg-neutral/5"
          onClick={() => handleEditCost(cost)}
        >
          <Row className="items-start justify-between">
            <Col gap2>
              <Row gap2 className="items-center">
                <span className="text-sm font-medium">{cost.startDate}</span>
                {cost.endDate && (
                  <span className="text-sm text-neutral/60">
                    - {cost.endDate}
                  </span>
                )}
              </Row>
              <div className="text-xs text-neutral/60">
                {t.freightLanes.costs.fields.rateFormula}: {cost.rateFormula}
              </div>
              <div className="text-xs text-neutral/60">
                {t.freightLanes.costs.fields.currency}: {cost.currency}
              </div>
              {cost.fuelSurchargeFormula && (
                <div className="text-xs text-neutral/60">
                  {t.freightLanes.costs.fields.fuelSurchargeFormula}:{' '}
                  {cost.fuelSurchargeFormula}
                </div>
              )}
            </Col>
            <Button
              variant="ghost"
              size="small"
              color="failure"
              onPress={() => handleDeleteCost(cost.id)}
              isPending={isDeleting}
            >
              <LucideTrash2 size={14} />
            </Button>
          </Row>
        </div>
      ))}
    </Col>
  );
};
